import { useEffect, useState } from 'react';
import BlogNavigation from '../blog/components/BlogNavigation';
import Footer from '../home/components/Footer';
import SupportHero from './components/SupportHero';
import SupportCategories from './components/SupportCategories';
import SupportFAQ from './components/SupportFAQ';
import SupportContact from './components/SupportContact';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://jobos.dev';

export default function SupportPage() {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    document.title = 'Support Center — JobOS | Help, FAQs & Contact';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'JobOS Support Center. Find answers to common questions about ATS autofill, Chrome extension, AI resume tailoring, billing, and more. Contact our team anytime.');
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'Support Center — JobOS | Help, FAQs & Contact', true);
    setMeta('og:url', `${SITE_URL}/support`, true);
    setMeta('og:type', 'website', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/support`);

    document.querySelectorAll('script[data-schema-page="support"]').forEach(el => el.remove());
    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Support Center — JobOS',
        url: `${SITE_URL}/support`,
        description: 'JobOS Support Center. Find answers, troubleshoot issues, or contact our team.',
        publisher: { '@type': 'Organization', name: 'JobOS', url: SITE_URL },
        inLanguage: 'en-US',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Support', item: `${SITE_URL}/support` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Why is the Chrome extension not autofilling my application?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Make sure your Master Profile is complete (at least 80% filled). Then click the JobOS icon on the job page and hit "Autofill Now". If the form still doesn\'t fill, the ATS may have updated its layout — please report it and we\'ll push a fix within 48 hours.',
            },
          },
          {
            '@type': 'Question',
            name: 'How do I cancel my Pro subscription?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Go to Settings → Billing → "Cancel Plan". Your Pro features remain active until the end of the current billing period.',
            },
          },
          {
            '@type': 'Question',
            name: 'Does JobOS work with Naukri.com and Rozee.pk?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes! JobOS natively supports Naukri.com, Rozee.pk, and 7+ other global ATS platforms.',
            },
          },
        ],
      },
    ];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-schema-page', 'support');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-schema-page="support"]').forEach(el => el.remove());
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0E1A] text-gray-900 dark:text-[#F8FAFC]">
      {/* Reading progress bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 dark:bg-gray-800 z-[60]">
        <div
          className="h-full bg-gradient-to-r from-[#6366F1] to-[#818CF8] transition-all duration-150"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      <BlogNavigation />

      <main className="pt-20">
        <SupportHero />

        {/* Quick links bar */}
        <div className="w-full border-b border-gray-100 dark:border-white/5 bg-white dark:bg-[#0A0E1A]">
          <div className="max-w-[1280px] mx-auto px-6 py-4 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <i className="ri-customer-service-2-line text-[#6366F1]"></i>
              <span className="font-medium text-gray-700 dark:text-gray-300">Support Hours:</span>
              <span>Mon–Fri, 9am–6pm EST</span>
            </div>
            <div className="w-px h-4 bg-gray-200 dark:bg-white/10 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                <i className="ri-checkbox-circle-line text-xs"></i>
                All Systems Operational
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] dark:text-[#818CF8] text-xs font-bold">
                <i className="ri-time-line text-xs"></i>
                &lt;24h Response
              </span>
            </div>
            <div className="ml-auto hidden md:flex items-center gap-4">
              <a href="https://app.jobos.dev/docs" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 dark:text-gray-400 hover:text-[#6366F1] transition-colors flex items-center gap-1">
                <i className="ri-book-open-line"></i> Docs
              </a>
              <a href="https://app.jobos.dev/roadmap" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 dark:text-gray-400 hover:text-[#6366F1] transition-colors flex items-center gap-1">
                <i className="ri-map-2-line"></i> Roadmap
              </a>
              <a href="mailto:hello@jobos.dev" className="text-xs text-gray-500 dark:text-gray-400 hover:text-[#6366F1] transition-colors flex items-center gap-1">
                <i className="ri-mail-line"></i> hello@jobos.dev
              </a>
            </div>
          </div>
        </div>

        <SupportCategories />
        <SupportFAQ />
        <SupportContact />
      </main>

      <Footer />
    </div>
  );
}
